/**
 * Confirmation dialog for hard-deleting a task.
 *
 * Hard delete is destructive: every related row in `task_events`,
 * `task_context`, and `pending_inputs` cascades. The API additionally writes
 * a snapshot to `deletions_audit` and an `audit_actions` row marked
 * `destructive` before removing the task — those rows are never auto-pruned.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Trash2 } from 'lucide-react';

interface Props {
  taskId: string;
  taskName: string;
  /** Called after the delete succeeds and before navigation away. */
  onDeleted?: () => void;
}

export function DeleteTaskDialog({ taskId, taskName, onDeleted }: Props): JSX.Element {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');

  const del = useMutation({
    mutationFn: () => api.deleteTask(taskId, reason.trim() || undefined),
    onSuccess: () => {
      setOpen(false);
      onDeleted?.();
      navigate('/tasks');
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="destructive" size="sm">
          <Trash2 className="h-4 w-4 mr-1.5" />
          Delete task
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete this task?</DialogTitle>
          <DialogDescription>
            <span className="block">
              This will permanently delete <span className="font-medium">{taskName}</span> and{' '}
              every event, pause, and context attachment for it. This cannot be undone.
            </span>
            <span className="block mt-2 text-xs">
              An audit record (snapshot, who, when, why) is preserved and never auto-pruned.
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="delete-reason" className="text-xs text-muted-foreground">
            Reason (optional, recorded in the audit log)
          </Label>
          <Textarea
            id="delete-reason"
            rows={2}
            placeholder="e.g. test data cleanup"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>

        {del.isError && (
          <p className="text-sm text-destructive">Failed to delete: {String(del.error)}</p>
        )}

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" disabled={del.isPending}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="destructive"
            disabled={del.isPending}
            onClick={() => del.mutate()}
          >
            {del.isPending ? 'Deleting…' : 'Delete permanently'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
